import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../App";
import BannerCarousel from "../components/BannerCarousel";
import StatsSection from "../components/StatsSection";

// Icons
import { MdArrowForwardIos } from "react-icons/md";
import ProductCard from "./ProductCard";
import CitiesSection from "../components/CitiesSection";
import VendorSection from "../components/VendorSection";
import Ribbon from "../components/Ribbon";
import CategoryGrid from "../components/CategoryGrid";


// API Endpoints - REMOVED vendor endpoints
const API_BASE_URL = "https://server.havbit.in/api";
const PRODUCTS_API = `${API_BASE_URL}/public/products`;
const CATEGORIES_API = `${API_BASE_URL}/categories`;
const SUBCATEGORIES_API = `${API_BASE_URL}/subcategories`;

export default function  Home() {
    const navigate = useNavigate();
    const {
        cart = [],
        addToCart,
        updateQty,
        removeFromCart,
        wishlist = [],
        toggleWishlistItem,
        selectedHomeCategory,
        setSelectedHomeCategory
    } = useCart();

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [categoryMap, setCategoryMap] = useState({});
    const [subcategoryMap, setSubcategoryMap] = useState({});
    const [groupedProducts, setGroupedProducts] = useState({});
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(true);
    const [error, setError] = useState(null);

    const cartQuantities = cart.reduce((acc, item) => {
        acc[item._id] = item.qty || 1;
        return acc;
    }, {});

    const isProductInWishlist = (productId) => {
        return wishlist.some(item => item._id === productId);
    };

    useEffect(() => {
        setSelectedHomeCategory(null);
        fetchAllData();
    }, []);

    // Function to get category name by ID (only from admin categories)
    const getCategoryNameById = (categoryId) => {
        if (!categoryId) return "Uncategorized";
        
        const adminCategory = categories.find(cat => cat._id === categoryId);
        if (adminCategory) return adminCategory.name;
        
        return "Uncategorized";
    };

    // Function to get subcategory name by ID (only from admin subcategories)
    const getSubcategoryNameById = (subcategoryId) => {
        if (!subcategoryId) return "";
        
        const adminSubcategory = subcategories.find(sub => sub._id === subcategoryId);
        if (adminSubcategory) return adminSubcategory.name;
        
        return "";
    };

    const fetchAllData = async () => {
        try {
            setLoading(true);

            // Start every request together, but do not hold the first paint for products.
            const categoriesRequest = Promise.all([
                fetch(CATEGORIES_API),
                fetch(SUBCATEGORIES_API)
            ]);
            const productsRequest = fetch(PRODUCTS_API);

            const [categoriesRes, subcategoriesRes] = await categoriesRequest;

            if (!categoriesRes.ok) throw new Error("Failed to fetch categories");
            if (!subcategoriesRes.ok) throw new Error("Failed to fetch subcategories");

            const categoriesData = await categoriesRes.json();
            const subcategoriesData = await subcategoriesRes.json();

            // Set data - only admin categories and subcategories
            setCategories(categoriesData);
            // Note: subcategories API returns {success: true, data: [...]}
            setSubcategories(subcategoriesData.data || []);
            
            // Create category map for quick lookup
            const categoryMap = {};
            categoriesData.forEach(cat => {
                categoryMap[cat._id] = cat.name;
            });
            setCategoryMap(categoryMap);
            
            // Create subcategory map
            const subcategoryMap = {};
            (subcategoriesData.data || []).forEach(sub => {
                subcategoryMap[sub._id] = sub.name;
            });
            setSubcategoryMap(subcategoryMap);

            setLoading(false);

            const productsRes = await productsRequest;
            if (!productsRes.ok) throw new Error("Failed to fetch products");
            const productsResult = await productsRes.json();

            // Extract products array
            const productsArray = productsResult.data || [];
            
            // Extract unique vendors from products
            const vendorSet = new Set();
            productsArray.forEach(product => {
                if (product.restaurantName && product.restaurantName.trim() !== "") {
                    vendorSet.add(product.restaurantName.trim());
                }
            });
            setVendors(Array.from(vendorSet));

            // Keep legacy products without a category visible on the home page.
            const filteredProducts = productsArray;

            // Group products by category name
            const grouped = {};

            filteredProducts.forEach(product => {
                const categoryId = product.category;
                const categoryName = categoryMap[categoryId] || product.categoryName || "Uncategorized";
                
                const subcategoryName = product.subcategory ? subcategoryMap[product.subcategory] : "";
                
                if (!grouped[categoryName]) {
                    grouped[categoryName] = [];
                }

                // Calculate discount
                let discount = 0;
                if (product.oldPrice && product.price && product.oldPrice > product.price) {
                    discount = Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100);
                }

                // Enhanced product data
                const enhancedProduct = {
                    ...product,
                    id: product.id || product._id,
                    name: product.name || "Product",
                    price: product.price || 0,
                    originalPrice: product.oldPrice || product.price * 1.2 || 0,
                    image: product.image || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400",
                    logo: product.logo || "",
                    category: categoryName,
                    subcategory: subcategoryName,
                    categoryId: categoryId,
                    subcategoryId: product.subcategory,
                    vendor: product.restaurantName || "Unknown Vendor",
                    rating: parseFloat((Math.random() * 1.5 + 3.5).toFixed(1)),
                    reviewCount: Math.floor(Math.random() * 1000) + 50,
                    stock: product.stock || 0,
                    weight: product.customWeight || product.size || "100g",
                    discount: discount,
                    quality: product.quality || "Good",
                    description: product.description || "",
                    source: product.vendor ? "vendor" : "admin",
                    vendorId: product.vendor
                };

                grouped[categoryName].push(enhancedProduct);
            });

            // Sort categories by number of products (descending)
            const sortedGrouped = {};
            Object.keys(grouped)
                .sort((a, b) => grouped[b].length - grouped[a].length)
                .forEach(key => {
                    sortedGrouped[key] = grouped[key];
                });

            setGroupedProducts(sortedGrouped);
            setProductsLoading(false);
        } catch (error) {
            setError(error.message);
            setLoading(false);
            setProductsLoading(false);
        }
    };

    const handleAddToCart = (product, quantity = 1) => {
        const cartItem = { 
            _id: product.id, 
            name: product.name, 
            price: product.price, 
            image: product.image, 
            qty: quantity 
        };
        addToCart(cartItem, quantity);
    };

    const handleQuantityUpdate = (productId, change) => {
        const currentQty = cartQuantities[productId] || 0;
        const newQty = currentQty + change;
        newQty < 1 ? removeFromCart(productId) : updateQty(productId, newQty);
    };

    const handleToggleWishlist = (product) => {
        toggleWishlistItem({ 
            _id: product.id, 
            name: product.name,
            price: product.price,
            image: product.image,
            originalPrice: product.originalPrice,
            category: product.category,
            discount: product.discount
        });
    };

    const handleCategoryClick = (categoryName) => {
        const category = categories.find(cat => cat.name === categoryName);
        setSelectedHomeCategory(category || { name: categoryName });
    };

    if (error) return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="text-red-600">Error: {error}</div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <main className="mx-auto px-2 py-2 pb-16 max-w-7xl">
                <BannerCarousel />
                {/* <StatsSection /> */}
                <div>
                    {/* <CitiesSection /> */}
                    
                    {/* <VendorSection vendors={vendors} /> */}
                </div>

                {productsLoading && Object.keys(groupedProducts).length === 0 ? (
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" aria-label="Loading products">
                        {Array.from({ length: 10 }).map((_, index) => (
                            <div key={index} className="h-64 rounded-lg bg-white animate-pulse" />
                        ))}
                    </div>
                ) : Object.keys(groupedProducts).length === 0 ? (
                    <div className="mt-6 text-center text-gray-500">
                        No products found. Please check back later.
                    </div>
                ) : selectedHomeCategory ? (
                    <section className="mt-6">
                        <div className="flex items-center justify-between mb-3 px-1">
                            <div className="flex items-center gap-1.5">
                                <div className="w-1 h-4 bg-red-500 rounded-full"></div>
                                <h2 className="text-base font-bold text-gray-900">{selectedHomeCategory.name}</h2>
                            </div>
                            <button
                                onClick={() => setSelectedHomeCategory(null)}
                                className="text-gray-500 font-medium text-xs"
                            >
                                Show All
                            </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                            {(groupedProducts[selectedHomeCategory.name] || []).map((product) => (
                                <ProductCard
                                    key={product.id}
                                    product={product}
                                    onAddToCart={handleAddToCart}
                                    onToggleWishlist={handleToggleWishlist}
                                    onQuantityUpdate={handleQuantityUpdate}
                                    isWishlisted={isProductInWishlist(product.id)}
                                    cartQuantity={cartQuantities[product.id] || 0}
                                    navigate={navigate}
                                />
                            ))}
                        </div>
                        {(groupedProducts[selectedHomeCategory.name] || []).length === 0 && (
                            <p className="py-8 text-center text-sm text-gray-500">No products found in this category.</p>
                        )}
                    </section>
                ) : (
                    Object.entries(groupedProducts).map(([categoryName, items]) => (
                        <section key={categoryName} className="mt-6">
                            <div className="flex items-center justify-between mb-2 px-1">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-1 h-3.5 bg-red-500 rounded-full"></div>
                                    <h3 className="text-sm font-bold text-gray-900">{categoryName}</h3>
                                </div>
                                <button 
                                    onClick={() => handleCategoryClick(categoryName)} 
                                    className="text-red-600 font-medium text-xs flex items-center"
                                >
                                    View All <MdArrowForwardIos className="ml-1 text-[8px]" />
                                </button>
                            </div>

                            {/* Horizontal Scroll on Mobile, Grid on Desktop */}
                            <div className="flex overflow-x-auto sm:grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 scrollbar-hide pb-2">
                                {items.slice(0, 6).map((product) => (
                                    <div key={product.id} className="product-snap-card flex-shrink-0 sm:flex-shrink">
                                        <ProductCard
                                            product={product}
                                            onAddToCart={handleAddToCart}
                                            onToggleWishlist={handleToggleWishlist}
                                            onQuantityUpdate={handleQuantityUpdate}
                                            isWishlisted={isProductInWishlist(product.id)}
                                            cartQuantity={cartQuantities[product.id] || 0}
                                            navigate={navigate}
                                        />
                                    </div>
                                ))}
                                
                                {/* View All Card */}
                                
                            </div>
                        </section>
                    ))
                )}
                {/* <CategoryGrid categories={categories} /> */}
            </main>

            <style>{`
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }

                /* Apply widths ONLY to product cards, not the whole site */
                @media (max-width: 639px) {
                    .product-snap-card {
                        width: 160px !important; /* Fixed width for consistent mobile scrolling */
                        margin-left: 4px;
                    }
                }
                
                /* Desktop: Reset width to allow grid to function */
                @media (min-width: 640px) {
                    .product-snap-card {
                        width: auto !important;
                    }
                }
            `}</style>
            <StatsSection />
        </div>
    );
}